<template>
  <section class="mt-7" data-testid="cockpit-constraints-list">
    <h2 class="cockpit-section-title">
      {{ $t('UserProjects.cockpit.constraints.title') }}
    </h2>
    <div class="cockpit-panel">
      <div v-if="!visible.length" class="text-sm text-[var(--color-disabled)] py-2">
        {{ $t('UserProjects.cockpit.constraints.empty') }}
      </div>
      <div v-else class="space-y-2">
        <button
          v-for="item in visible"
          :key="item.id"
          type="button"
          class="w-full flex items-center gap-3 p-3 rounded-[10px] border border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors text-left"
          @click="$emit('view-all')"
        >
          <span class="cockpit-type-badge">
            {{ typeLabel(item.type) }}
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-[13px] font-medium text-[var(--color-dark)] truncate m-0">
              {{ item.title || item.description || '—' }}
            </p>
            <p class="text-[12px] text-[var(--color-disabled)] m-0 mt-0.5">
              {{ item.ownerDisplayName || '—' }}
              <template v-if="item.daysOpen != null">
                · {{ $t('UserProjects.cockpit.constraints.daysOpen', { days: item.daysOpen }) }}
              </template>
            </p>
          </div>
        </button>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div
          v-for="stat in scopeStats"
          :key="stat.key"
          class="rounded-[10px] p-3 bg-[#FAFAFA] border border-[#E5E5E5]"
        >
          <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] mb-1 m-0">
            {{ stat.label }}
          </p>
          <p class="text-[20px] font-bold text-[var(--color-dark)] leading-tight m-0">
            {{ $t('UserProjects.cockpit.constraints.openCount', { count: stat.open }) }}
          </p>
          <p class="text-[12px] text-[var(--color-disabled)] mt-0.5 mb-0">
            {{
              stat.avgDays != null
                ? $t('UserProjects.cockpit.constraints.avgDays', { days: stat.avgDays })
                : '—'
            }}
          </p>
        </div>
      </div>

      <div class="flex items-center justify-between mt-4 pt-3 border-t border-[#F0F0F0]">
        <p class="text-[12px] text-[var(--color-disabled)] m-0">
          {{ summaryLine }}
        </p>
        <button
          type="button"
          class="cockpit-link-btn"
          data-testid="cockpit-all-constraints"
          @click="$emit('view-all')"
        >
          {{ $t('UserProjects.cockpit.constraints.viewAll') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CockpitOpenConstraintsList',
  props: {
    constraints: { type: Array, default: () => [] },
    summary: { type: Object, default: null },
  },
  emits: ['view-all'],
  computed: {
    visible() {
      return (this.constraints || []).slice(0, 5);
    },
    scopeStats() {
      const summary = this.summary || {};
      return [
        {
          key: 'intern',
          label: this.$t('UserProjects.cockpit.constraints.types.intern'),
          open: summary.intern?.openCount ?? 0,
          avgDays: summary.intern?.avgDaysOpen ?? null,
        },
        {
          key: 'cross',
          label: this.$t('UserProjects.cockpit.constraints.types.cross'),
          open: summary.cross?.openCount ?? 0,
          avgDays: summary.cross?.avgDaysOpen ?? null,
        },
        {
          key: 'extern',
          label: this.$t('UserProjects.cockpit.constraints.types.extern'),
          open: summary.extern?.openCount
            ?? this.constraints.filter((c) => c.type === 'extern').length,
          avgDays: summary.extern?.avgDaysOpen ?? null,
        },
      ];
    },
    summaryLine() {
      return this.scopeStats
        .map((stat) => `${stat.open} ${stat.label.toLowerCase()}`)
        .join(' · ');
    },
  },
  methods: {
    typeLabel(type) {
      if (type === 'bereichsuebergreifend') {
        return this.$t('UserProjects.cockpit.constraints.types.cross');
      }
      if (type === 'extern') {
        return this.$t('UserProjects.cockpit.constraints.types.extern');
      }
      return this.$t('UserProjects.cockpit.constraints.types.intern');
    },
  },
};
</script>

<style scoped>
.cockpit-section-title {
  margin: 0 0 0.75rem;
  font-size: 13px;
  color: #ababab;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
}

.cockpit-panel {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.cockpit-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  background: #f5f5f5;
  color: #6b6b6b;
  flex-shrink: 0;
}

.cockpit-link-btn {
  border: none;
  background: transparent;
  color: #6b6b6b;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}

.cockpit-link-btn:hover {
  text-decoration: underline;
}
</style>
